import { OverlayTrigger, Popover } from 'react-bootstrap';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import React from 'react';
import { EquivalenceCharges } from '../../../user/domain/User.model';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { ProductAvailabilityUI } from '../../../product/ui/ProductAvailabilityUI';
import { StockBypassNotice } from '../../../product/ui/StockBypassNotice';
import { onDemandUnits } from '../../domain/onDemandSplit';
import { FormattedNumber } from "../../../common/ui/FormattedNumber";
import { AddToPendingButton } from '../AddToPendingButton';

export const CartProductUI = ({ data, actions }) => {
  const { product, productDiscount, promotionDiscount, userEquivalenceCharges, isDeprecated = false, packParentName } = data;
  const { edit, remove, changeProductEquivalenceCharges } = actions;
  // Las líneas bypass partidas entre zona normal y "Bajo pedido" enseñan aquí
  // solo su parte; la cantidad real de la línea sigue siendo product.quantity.
  const displayQuantity = data.displayQuantity ?? product.quantity;
  // Componente de pack generado por el servidor: gratis, ligado al padre y sin acciones.
  const isPackGift = product.packOrigin === true;

  const packGiftPopOver = (
    <Popover>
      <Popover.Header>Complemento incluido</Popover.Header>
      <Popover.Body>
        <div className="d-flex flex-column align-products-start gap-1">
          <span>Valor: <strong><FormattedNumber value={product.basePrice} config={{ style: "currency" }} /></strong></span>
          <span className="text-success fw-bold" style={{ fontSize: '0.88rem' }}>Incluido sin coste con el producto principal</span>
          <span className="text-muted" style={{ fontSize: '0.8rem' }}>
            La cantidad va ligada al producto principal y se ajusta con él.
          </span>
        </div>
      </Popover.Body>
    </Popover>
  );

  const priceDetailsPopOver = (
    <Popover>
      <Popover.Header>Detalles del precio</Popover.Header>
      <Popover.Body>
        <div className="d-flex flex-column align-products-start gap-1">
          <span>Precio catálogo: <strong><FormattedNumber value={product.basePrice} config={{ style: "currency" }} /></strong></span>
          <span>Precio final: <strong><FormattedNumber value={product.sellingPrice} config={{ style: "currency" }} /></strong></span>
          {promotionDiscount > 0 ? (
            <span className="text-success fw-bold" style={{ fontSize: '0.88rem' }}>
              Dto. total aplicado: <strong>
                <FormattedNumber
                  value={product.basePrice > 0 ? (1 - (product.sellingPrice / product.basePrice)) * 100 : 0}
                  config={{ style: "percent" }}
                />
              </strong>
            </span>
          ) : (
            <span className="text-muted" style={{ fontSize: '0.8rem' }}>
              Dto. negociado: <strong>
                <FormattedNumber
                  value={product.basePrice > 0 ? (1 - (product.sellingPrice / product.basePrice)) * 100 : 0}
                  config={{ style: "percent" }}
                />
              </strong>
            </span>
          )}
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <div className={`card ${isPackGift ? 'ms-4 border-start border-success border-3' : ''}`}>
      <div className="d-flex flex-lg-row flex-column">
        <div className="d-flex justify-content-center align-items-center"
          style={{ width: '150px' }}>
          {
            product.packParentId === null || isPackGift ? (
              <img src={product.image} className="img-fluid rounded-start p-2" alt="imagen producto" />
            ) : (
              <CardGiftcardIcon className="text-primary" fontSize="large" />
            )
          }
        </div>
        <div className="card-body">
          <div className="row gap-4 gap-lg-0">
            <div className="col-12 col-lg-4 col-xxl-5">
              <div className="d-grid gap-1">
                <span>
                  {isPackGift && (
                    <span className="badge bg-success me-2">INCLUIDO</span>
                  )}
                  {product.name}
                </span>
                <small className="text-muted">REF: {product.code}</small>
                {isPackGift && packParentName && (
                  <small className="text-success">Complemento de {packParentName}</small>
                )}
                {/* La pastilla roja de bajo pedido solo cuando de verdad falta stock para la
                    linea: BC marca el codigo bypass a nivel de marca y hay cientos de productos
                    bypass CON stock, donde la pastilla asustaba sin motivo (queja comercial 27-jul). */}
                {product.stockBypass && onDemandUnits(product) > 0 ? (
                  <StockBypassNotice product={product}
                    variant="pill"
                    message={product.outOfStockMessage}
                    requestedQuantity={product.quantity} />
                ) : (
                  <ProductAvailabilityUI availableUnits={product.stock}
                    insufficientUnitsMsg={product.outOfStockMessage}
                    requiredQuantity={product.quantity}
                    showStock={product.showStock}
                  />
                )}
              </div>
            </div>
            <div className="col-12 col-lg-8 col-xxl-7">
              <div className="row text-center gap-2 gap-sm-0 justify-content-center justify-content-xl-end">
                <div className="col-5 col-md-auto">
                  <div className="d-flex flex-column">
                    <span>Precio unitario</span>
                    <div className="d-flex flex-row gap-2 justify-content-center">
                      {isPackGift ? (
                        <span className="text-success fw-bold">Incluido</span>
                      ) : (
                        <FormattedNumber value={product.sellingPrice} config={{ style: "currency" }} />
                      )}
                      <OverlayTrigger placement="bottom"
                        trigger={['hover', 'focus']}
                        overlay={isPackGift ? packGiftPopOver : priceDetailsPopOver}>
                        <InfoTwoToneIcon style={{ cursor: 'pointer', width: '2rem' }} />
                      </OverlayTrigger>
                    </div>
                  </div>
                </div>
                <div className="col-5 col-md-auto">
                  <div className={`d-grid ${displayQuantity > product.stock ? 'text-danger' : ''}`}>
                    <span >Unidades</span>
                    <span>{displayQuantity}</span>
                  </div>
                </div>
                <div className="col-5 col-md-auto">
                  <div className="d-grid">
                    <span>Sub total (Sin IVA)</span>
                    <FormattedNumber value={product.sellingPrice * displayQuantity} config={{ style: "currency" }} />
                  </div>
                </div>
                <div className="col-5 col-md-auto">
                  <div className="d-grid">
                    <span>IVA</span>
                    <FormattedNumber value={product.tax} config={{ style: "percent" }} />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="mt-2 d-flex flex-row justify-content-end align-items-center">
                {
                  userEquivalenceCharges === EquivalenceCharges.BY_CHOICE && !product.packParentId && (
                    <div className="flex-fill">
                      <button className="btn btn-info" onClick={changeProductEquivalenceCharges}>
                        {
                          product.equivalenceCharge ? (
                            <span>Quitar recargo de equivalencia</span>
                          ) : (
                            <span>Añadir recargo de equivalencia</span>
                          )
                        }
                      </button>
                    </div>
                  )
                }
                {
                  product.packParentId === null && (
                    <div className="d-flex flex-row justify-content-end gap-2">
                      {
                        isDeprecated && product.quantity > product.stock && (
                          <AddToPendingButton product={product} />
                        )
                      }
                      <button type="button"
                        className={`btn ${isDeprecated ? 'btn-warning' : 'btn-primary'}`}
                        onClick={() => edit()}>
                        Editar
                      </button>
                      <button type="button"
                        className="btn btn-danger"
                        onClick={() => remove()}>
                        Borrar
                      </button>
                    </div>
                  )
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};